import { plural, t } from '@lingui/macro'
import { Form, Formik } from 'formik'
import { useMemo } from 'react'
import * as yup from 'yup'

import { Flex, Padding, Spacing } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H1 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { createYupSchema } from '@/modules/shared/helpers/create-yup-schema'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { SignableDocumentMessage } from '../../components/signable-document-message/SignableDocumentMessage'
import { SignableDocumentQuestion } from '../../components/signable-document-question/SignableDocumentQuestion'
import { fieldsToSchema } from './helpers/generate-schema-from-fields'
import { StForm } from './SignableFormActivity.styled'

import type { SignableDocumentDetailActivity } from '../../types/documents.types'

type Props = {
  inputs: SignableDocumentDetailActivity['inputs']
  fields: SignableDocumentDetailActivity['definition']['fields']
  onNext: (values: object) => void
  numDocuments: number
  messages: SignableDocumentDetailActivity[]
}

export const SignableFormActivity = ({ inputs, fields, messages, numDocuments, onNext }: Props) => {
  const isLoggedInForMandatee = useLoggedInForMandatee()

  const formInitialValues = useMemo(() => {
    return (
      fields?.reduce(
        (accumulator, current) => {
          const { id } = current
          const input = inputs.find((input) => input.fieldId === id)

          accumulator[id] = current.type === 'checkbox' ? input?.value || false : input?.value || ''
          return accumulator
        },
        {} as Record<string, unknown>,
      ) || {}
    )
  }, [fields, inputs])

  const formValidationSchema = useMemo(() => {
    if (fields) {
      return fieldsToSchema(fields).reduce(createYupSchema, {})
    }
    return {}
  }, [fields])

  const handleSubmit = (values: unknown) => {
    mixpanel.track('document_signing_review', {
      is_mandate: isLoggedInForMandatee,
    })
    onNext(values as object)
  }

  // Group fields and messages together and sort by order
  const formItems = useMemo(() => {
    return [...fields, ...messages].sort((a, b) => {
      return (a.overallOrder ?? 0) - (b.overallOrder ?? 0)
    })
  }, [fields, messages])

  return (
    <Flex alignContent='center' justifyContent='center'>
      <Formik
        initialValues={formInitialValues}
        validationSchema={yup.object().shape(formValidationSchema)}
        validateOnChange
        validateOnMount
        onSubmit={handleSubmit}
      >
        {({ isValid }) => (
          <Form>
            <StForm>
              <H1>
                {t({
                  id: 'documents.signable.title',
                  message: 'Vragenlijst',
                })}
              </H1>

              {formItems?.map((item) => {
                if ('definition' in item) {
                  return <SignableDocumentMessage key={item.id} message={item} />
                }
                return <SignableDocumentQuestion key={item.id} data={item} />
              })}

              <div>
                <Flex justifyContent='center'>
                  <Padding px={20} pt={20}>
                    <Span type='BodyMediumRegular' textAlign='center'>
                      {t({
                        id: 'documents.signable.check.message',
                        message: 'Controleer de documenten voor opname en onderteken hierna je verklaring.',
                      })}
                    </Span>
                  </Padding>
                </Flex>
                <Spacing height={20} />
                <Button variant='Primary' type='submit' disabled={!isValid}>
                  <Flex justifyContent='center' alignItems='center'>
                    {t({
                      id: 'document.signable.button.label',
                      message: plural(numDocuments, {
                        one: `Controleer document`,
                        other: `Controleer documenten`,
                      }),
                    })}
                  </Flex>
                </Button>
                <Spacing height={40} />
              </div>
            </StForm>
          </Form>
        )}
      </Formik>
    </Flex>
  )
}
